import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api } from '../../utils/api';
import { useStore } from '../../store';
import joyAvatar from '../../assets/joy.png';

// Joy — the virtual accountant's floating avatar (P1) + her live to-do feed (P2). App-level, draggable,
// with an attention badge. HARD non-interference contract (docs/joy-design.md §2): never steals focus,
// never a blocking overlay, never interrupts the user's typing / uploads / open dialogs, never refreshes
// or navigates on its own. A small corner widget (z-index BELOW modals) the user can ignore or move.

type JoyState = { enabled: boolean; office: boolean; visible: boolean; badge: number };
type FeedItem = { client_id: string; client: string; task_kind: string; label: string; period: string; due_date: string; status: string; overdue: boolean };
type Risk = { client_id: string; client: string; type: string; level: string; reason: string };
type Feed = {
  visible: boolean;
  pendingConfirm: { id: string; summary: string; channel: string }[];
  waitingClient: FeedItem[]; waitingUs: FeedItem[]; risks: Risk[];
  counts: { confirm: number; client: number; us: number; risks: number };
};
const POS_KEY = 'joy_widget_pos';

export default function JoyWidget() {
  const [state, setState] = useState<JoyState | null>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ right: number; bottom: number }>(() => {
    try { const p = JSON.parse(localStorage.getItem(POS_KEY) || 'null'); if (p && typeof p.right === 'number') return p; } catch { /* */ }
    return { right: 24, bottom: 24 };
  });
  const drag = useRef({ active: false, moved: false, sx: 0, sy: 0, r0: 0, b0: 0 });
  const posRef = useRef(pos); posRef.current = pos;

  // Light poll of Joy's state — never blocks, never reloads the page.
  useEffect(() => {
    let alive = true;
    const load = () => api.getJoyState().then((s: any) => { if (alive) setState(s); }).catch(() => { /* silent */ });
    load();
    const id = window.setInterval(load, 60000);
    return () => { alive = false; window.clearInterval(id); };
  }, []);

  const onPointerDown = (e: React.PointerEvent) => {
    drag.current = { active: true, moved: false, sx: e.clientX, sy: e.clientY, r0: pos.right, b0: pos.bottom };
    try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch { /* */ }
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current; if (!d.active) return;
    const dx = e.clientX - d.sx, dy = e.clientY - d.sy;
    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) d.moved = true;
    if (d.moved) {
      const right = Math.max(8, Math.min(window.innerWidth - 64, d.r0 - dx));
      const bottom = Math.max(8, Math.min(window.innerHeight - 64, d.b0 - dy));
      setPos({ right, bottom });
    }
  };
  const onPointerUp = () => {
    const d = drag.current;
    if (d.active && d.moved) localStorage.setItem(POS_KEY, JSON.stringify(posRef.current));
    else if (d.active && !d.moved) setOpen(o => !o);   // a click (no drag) toggles the panel
    d.active = false;
  };

  if (!state || !state.visible) return null;   // hidden for non-office users and when Joy is off

  const badge = state.badge;
  return (
    // z-index 9000 keeps Joy BELOW app modals (10000+) so she never covers an open dialog.
    <div style={{ position: 'fixed', right: pos.right, bottom: pos.bottom, zIndex: 9000 }}>
      {open && <JoyPanel onClose={() => setOpen(false)} />}
      {/* Outer box is NOT clipped, so the badge can sit on the circle's edge and stay fully visible. */}
      <div onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} title="Joy"
        style={{ width: 56, height: 56, position: 'relative', cursor: 'grab', touchAction: 'none', userSelect: 'none' }}>
        <div style={{ width: '100%', height: '100%', borderRadius: '50%', overflow: 'hidden', boxShadow: 'var(--shadow-md)', border: '2px solid #fff', boxSizing: 'border-box' }}>
          <img src={joyAvatar} alt="Joy" draggable={false} style={{ width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }} />
        </div>
        {badge > 0 && (
          <span style={{ position: 'absolute', top: -6, right: -6, zIndex: 2, minWidth: 20, height: 20, padding: '0 5px', borderRadius: 10, background: '#E24B4A', color: '#fff', fontSize: 11.5, fontWeight: 800, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 0 2px #fff', boxSizing: 'border-box' }}>{badge}</span>
        )}
      </div>
    </div>
  );
}

type Pending = { id: string; kind: string; channel: string; what: string; client: string; to: string[]; cc: string[]; subject: string; body_html: string; tg_text?: string; summary?: string };

// "Joy on duty" kiosk — what the joy@slf-accounting.co login sees: NO portal, just Joy. Lets anyone
// keep Joy parked on a spare screen/desktop, always working. The floating widget stays functional.
export function JoyKiosk() {
  const { t } = useTranslation();
  const { logout } = useStore();
  return (
    <div style={{ height: '100vh', width: '100vw', background: 'var(--bg-app)', position: 'relative', overflow: 'hidden' }}>
      <button onClick={logout} title={t('signOut')}
        style={{ position: 'absolute', top: 16, right: 16, zIndex: 10, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-surface)', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
        <i className="ti ti-logout" style={{ fontSize: 15 }} aria-hidden />{t('signOut')}
      </button>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', pointerEvents: 'none', userSelect: 'none' }}>
        <img src={joyAvatar} alt="" style={{ width: 88, height: 88, borderRadius: '50%', objectFit: 'cover', boxShadow: 'var(--shadow-md)', border: '3px solid #fff' }} />
        <div style={{ marginTop: 14, fontSize: 18, fontWeight: 800, color: 'var(--text-primary)' }}>Joy</div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>{t('joy_role')} · {t('joy_on_duty')}</div>
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 6 }}>{t('joy_kiosk_hint')}</div>
      </div>
      <JoyWidget />
    </div>
  );
}

type Question = { id: string; client: string; question: string; kind: string; options: { value: string; label: string }[] };

function JoyPanel({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [feed, setFeed] = useState<Feed | null>(null);
  const [pending, setPending] = useState<Pending[] | null>(null);
  const [questions, setQuestions] = useState<Question[] | null>(null);

  const load = () => {
    api.getJoyFeed().then((f: any) => setFeed(f)).catch(() => setFeed(null));
    api.getJoyPending().then((p: any) => setPending(p.rows || [])).catch(() => setPending([]));
    api.getJoyQuestions().then((q: any) => setQuestions(q.rows || [])).catch(() => setQuestions([]));
  };
  useEffect(() => { load(); }, []);   // eslint-disable-line

  const go = (path: string) => { onClose(); navigate(path); };   // navigation only on explicit click
  const c = feed?.counts;
  const confirmN = pending?.length || 0;
  const questN = questions?.length || 0;
  const loaded = !!feed && !!pending && !!questions;
  const empty = loaded && c && (confirmN + questN + c.client + c.us + c.risks) === 0;

  return (
    // Non-modal: no backdrop, doesn't block the page. Anchored above the avatar.
    <div style={{ position: 'absolute', bottom: 68, right: 0, width: 360, maxHeight: '74vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 14, boxShadow: 'var(--shadow-md)', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
        <img src={joyAvatar} alt="" style={{ width: 34, height: 34, borderRadius: '50%', objectFit: 'cover' }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--text-primary)' }}>Joy</div>
          <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{t('joy_role')}</div>
        </div>
        <button onClick={onClose} aria-label="close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', fontSize: 16 }}><i className="ti ti-x" /></button>
      </div>

      <div style={{ overflowY: 'auto', padding: '12px 14px' }}>
        {!loaded && <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" style={{ animation: 'spin 1s linear infinite' }} /></div>}
        {empty && (
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>{t('joy_greeting')}<div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8, color: '#15803D' }}><i className="ti ti-checks" />{t('joy_all_clear')}</div></div>
        )}
        {loaded && !empty && (
          <>
            {questN > 0 && (
              <Section icon="ti-help-circle" color="#7C3AED" title={t('joy_sec_decide')} count={questN} onAll={() => {}}>
                {questions!.map(q => <JoyDecideCard key={q.id} item={q} onChanged={load} />)}
              </Section>
            )}
            {confirmN > 0 && (
              <Section icon="ti-mail-cog" color="#B45309" title={t('joy_sec_confirm')} count={confirmN} onAll={() => {}}>
                {pending!.map(p => <JoyConfirmCard key={p.id} item={p} onChanged={load} />)}
              </Section>
            )}
            <Section icon="ti-user-question" color="#2B6CB0" title={t('joy_sec_client')} count={c!.client} onAll={() => go('/m/compliance')}>
              {feed!.waitingClient.slice(0, 8).map((it, i) => <Line key={i} text={`${it.client}`} sub={`${it.label}${it.period ? ' · ' + it.period : ''}`} due={it.due_date} overdue={it.overdue} onClick={() => go('/m/compliance')} />)}
            </Section>
            <Section icon="ti-briefcase" color="#6D28D9" title={t('joy_sec_us')} count={c!.us} onAll={() => go('/m/compliance')}>
              {feed!.waitingUs.slice(0, 8).map((it, i) => <Line key={i} text={`${it.client}`} sub={`${it.label}${it.period ? ' · ' + it.period : ''}`} due={it.due_date} overdue={it.overdue} onClick={() => go('/m/compliance')} />)}
            </Section>
            <Section icon="ti-alert-triangle" color="#E24B4A" title={t('joy_sec_risk')} count={c!.risks} onAll={() => go('/m/compliance')}>
              {feed!.risks.slice(0, 6).map((r, i) => <Line key={i} text={r.client} sub={`${r.type} · ${r.reason || r.level}`} onClick={() => go('/m/compliance')} />)}
            </Section>
          </>
        )}
      </div>
    </div>
  );
}

// One reviewable email Joy queued: collapsed shows who+what; expand to SEE the full email (to / subject
// / body), then Confirm & send, Edit (modify subject + body in place), or Reject.
const jlbl: React.CSSProperties = { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-tertiary)', display: 'block', marginBottom: 3 };
const jinp: React.CSSProperties = { width: '100%', padding: '6px 8px', fontSize: 12.5, border: '1px solid var(--border-strong)', borderRadius: 6, background: '#fff', boxSizing: 'border-box' };
const jbtn = (bg: string, color: string): React.CSSProperties => ({ padding: '6px 10px', borderRadius: 7, border: 'none', background: bg, color, fontSize: 11.5, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 });
function JField({ label, value }: { label: string; value: string }) {
  return <div style={{ marginBottom: 8 }}><label style={jlbl}>{label}</label><div style={{ fontSize: 12.5, color: 'var(--text-secondary)', wordBreak: 'break-word' }}>{value}</div></div>;
}
// A decision Joy needs made: she states the situation + offers buttons; one click answers it.
function JoyDecideCard({ item, onChanged }: { item: Question; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const answer = async (value: string) => {
    setBusy(true);
    try { await api.joyAnswer(item.id, value); onChanged(); } catch { setBusy(false); }
  };
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '9px 10px', background: '#fff' }}>
      {item.client && <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-primary)' }}>{item.client}</div>}
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5, margin: '3px 0 8px' }}>{item.question}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {item.options.map(o => (
          <button key={o.value} onClick={() => answer(o.value)} disabled={busy}
            style={{ padding: '6px 10px', borderRadius: 7, border: '1px solid #7C3AED', background: '#F5F3FF', color: '#6D28D9', fontSize: 11.5, fontWeight: 700, cursor: busy ? 'wait' : 'pointer' }}>{o.label}</button>
        ))}
      </div>
    </div>
  );
}

function JoyConfirmCard({ item, onChanged }: { item: Pending; onChanged: () => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [subject, setSubject] = useState(item.subject);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const isEmail = item.channel === 'email';

  const confirm = async () => {
    setBusy(true); setErr('');
    const edits = editing && isEmail ? { subject, bodyHtml: bodyRef.current?.innerHTML } : undefined;
    try { await api.joyConfirm(item.id, edits); onChanged(); }
    catch (e: any) { setErr(e.message || 'failed'); setBusy(false); }
  };
  const discard = async () => { setBusy(true); setErr(''); try { await api.joyDiscard(item.id); onChanged(); } catch (e: any) { setErr(e.message || 'failed'); setBusy(false); } };
  const cancelEdit = () => { setEditing(false); setSubject(item.subject); if (bodyRef.current) bodyRef.current.innerHTML = item.body_html; };

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', background: '#fff' }}>
      <div onClick={() => setOpen(o => !o)} style={{ padding: '8px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
        <i className={`ti ${isEmail ? 'ti-mail' : 'ti-brand-telegram'}`} style={{ color: '#B45309', fontSize: 14, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.client || (item.to || []).join(', ') || item.summary}</div>
          <div style={{ fontSize: 11, color: 'var(--text-tertiary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.what}</div>
        </div>
        <i className={`ti ti-chevron-${open ? 'up' : 'down'}`} style={{ color: 'var(--text-tertiary)', fontSize: 14, flexShrink: 0 }} />
      </div>
      {open && (
        <div style={{ borderTop: '1px solid var(--border)', padding: 10, background: 'var(--c-gray-50)' }}>
          {isEmail ? <>
            <JField label={t('joy_to')} value={(item.to || []).join(', ')} />
            {editing
              ? <div style={{ marginBottom: 8 }}><label style={jlbl}>{t('joy_subject')}</label><input value={subject} onChange={e => setSubject(e.target.value)} style={jinp} /></div>
              : <JField label={t('joy_subject')} value={subject} />}
            <label style={jlbl}>{t('joy_message')}</label>
            <div ref={bodyRef} contentEditable={editing} suppressContentEditableWarning dangerouslySetInnerHTML={{ __html: item.body_html }}
              style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 10px', maxHeight: 240, overflowY: 'auto', fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5, outline: editing ? '2px solid var(--accent)' : 'none' }} />
          </> : <div style={{ fontSize: 12.5, whiteSpace: 'pre-wrap', color: 'var(--text-secondary)' }}>{item.tg_text}</div>}
          {err && <div style={{ fontSize: 11.5, color: '#E24B4A', marginTop: 6 }}>{err}</div>}
          <div style={{ display: 'flex', gap: 6, marginTop: 10, alignItems: 'center' }}>
            <button onClick={confirm} disabled={busy} style={{ ...jbtn('#1E9E63', '#fff'), opacity: busy ? 0.6 : 1 }}><i className="ti ti-check" />{editing ? t('joy_send_edited') : t('joy_confirm_send')}</button>
            {isEmail && !editing && <button onClick={() => setEditing(true)} disabled={busy} style={jbtn('var(--c-gray-100)', 'var(--text-secondary)')}><i className="ti ti-pencil" />{t('joy_edit')}</button>}
            {editing && <button onClick={cancelEdit} disabled={busy} style={jbtn('var(--c-gray-100)', 'var(--text-secondary)')}>{t('cancel')}</button>}
            <div style={{ flex: 1 }} />
            <button onClick={discard} disabled={busy} style={jbtn('#FCEBEB', '#E24B4A')}><i className="ti ti-x" />{t('joy_reject')}</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ icon, color, title, count, onAll, children }: { icon: string; color: string; title: string; count: number; onAll: () => void; children: React.ReactNode }) {
  if (!count) return null;
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <i className={`ti ${icon}`} style={{ fontSize: 14, color }} />
        <span style={{ fontSize: 11.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.02em', color: 'var(--text-secondary)' }}>{title}</span>
        <span style={{ fontSize: 11, fontWeight: 800, color: '#fff', background: color, borderRadius: 8, padding: '0 6px', minWidth: 16, textAlign: 'center' }}>{count}</span>
        <div style={{ flex: 1 }} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>{children}</div>
    </div>
  );
}

function Line({ text, sub, due, overdue, onClick }: { text: string; sub?: string; due?: string; overdue?: boolean; onClick: () => void }) {
  const fmt = (ymd?: string) => { const p = String(ymd || '').split('-'); return p.length === 3 ? `${p[2]}/${p[1]}` : ''; };
  return (
    <div onClick={onClick} style={{ padding: '7px 9px', borderRadius: 8, background: 'var(--c-gray-50)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
      onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-active)')} onMouseLeave={e => (e.currentTarget.style.background = 'var(--c-gray-50)')}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{text}</div>
        {sub && <div style={{ fontSize: 11, color: 'var(--text-tertiary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub}</div>}
      </div>
      {due && <span style={{ fontSize: 11, fontWeight: 700, color: overdue ? '#E24B4A' : 'var(--text-tertiary)', flexShrink: 0 }}>{fmt(due)}</span>}
    </div>
  );
}
